import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star, Sparkles, Truck, ShieldCheck, RotateCcw, CreditCard,
  FlaskConical, Heart, Lightbulb, Layers, SlidersHorizontal, Feather, Timer, Usb, Check, Minus, BarChart3, GitCompareArrows } from "lucide-react";
import { IMG, fmt, CATALOG } from "@/data/catalog";
import { mediaUrl as M } from "@/data/pdpMedia";
import { AddToCartButton } from "@/components/common/ui";

const ease = [0.22, 1, 0.36, 1];
const reveal = { hidden: { opacity: 0, y: 26 }, show: { opacity: 1, y: 0, transition: { duration: 1, ease } } };
const stagger = { show: { transition: { staggerChildren: 0.1 } } };
const p = CATALOG.single;

/* ═══════════════ 1 · IMMERSIVE DARK HERO ═══════════════ */
export const HeroImmersive = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  return (
    <section ref={ref} className="relative overflow-hidden bg-[#232222]" data-testid="hero">
      <div className="mx-auto grid max-w-[1640px] grid-cols-1 items-center gap-y-4 px-6 pt-24 pb-12 md:min-h-[70vh] md:grid-cols-12 md:gap-x-8 md:px-12 md:pt-24 md:pb-8 lg:px-20">
        <motion.div variants={stagger} initial="hidden" animate="show" className="relative z-20 md:col-span-5">
          <motion.div variants={reveal} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-[#FBC4A8]" />
            <span className="text-[0.6rem] font-bold uppercase tracking-[0.22em] text-[#C9BBB2]">World's First Animal-Print LED Mask</span>
          </motion.div>
          <motion.h1 variants={reveal} className="mt-8 font-display text-[3.6rem] font-light leading-[0.86] tracking-[-0.02em] text-[#FFFCFA] sm:text-7xl lg:text-[5.8rem]">
            Wildly<br />effective.<br /><span className="font-script text-[#FBC4A8]">Visibly proven.</span>
          </motion.h1>
          <motion.p variants={reveal} className="mt-8 max-w-sm text-base font-light leading-relaxed text-[#C9BBB2] md:text-lg">
            Medical-grade light therapy, reimagined as the most luxurious ten minutes of your day, in a mask you'll never want to hide.
          </motion.p>
          <motion.div variants={reveal} className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-5">
            <Link to="/product/single" data-testid="primary-cta-button"
              className="group inline-flex items-center gap-3 rounded-full bg-[#E97C6A] px-9 py-4 text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-[#FFFCFA] transition-all duration-500 hover:bg-[#FBC4A8] hover:text-[#232222]">
              Discover the Mask <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1.5" />
            </Link>
            <div className="flex flex-col">
              <span className="font-display text-2xl font-light text-[#FFFCFA]">{fmt(p.price)} {p.compareAt > p.price && <span className="text-base text-white/40 line-through">{fmt(p.compareAt)}</span>}</span>
              <span className="text-[0.6rem] uppercase tracking-[0.16em] text-[#9A8E88]">or 4 interest-free payments</span>
            </div>
          </motion.div>
          <motion.div variants={reveal} className="mt-9 flex items-center gap-2.5">
            <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-[#FBC4A8] text-[#FBC4A8]" />)}</div>
            <span className="text-[0.7rem] uppercase tracking-[0.14em] text-[#9A8E88]">4.9 · loved by 50+ glow-getters</span>
          </motion.div>
        </motion.div>

        <div className="relative md:col-span-7 md:h-full">
          {/* soft LED glow disc (solid, blurred — not a gradient) */}
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-[80%] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E97C6A] opacity-25 blur-[120px]" />
          <motion.img style={{ y }} initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.3, ease }}
            src={M("home_hero")} alt="Aura Glow Leopard Luxe LED mask" data-testid="hero-image"
            className="relative z-10 mx-auto w-[88%] max-w-[640px] object-contain drop-shadow-[0_40px_80px_rgba(0,0,0,0.45)] md:w-full" />
        </div>
      </div>
    </section>
  );
};

/* ═══════════════ 2 · MARQUEE RIBBON ═══════════════ */
const RIBBON = ["World's First Animal-Print LED Mask", "114 Medical-Grade LEDs", "UV-Free · Heat-Free", "Clinically Proven", "10 Minutes a Day", "30-Day Money-Back"];
export const MarqueeRibbon = () => (
  <div className="overflow-hidden border-b border-[#F2E8E6] bg-[#FFFCFA] py-5" data-testid="marquee-ribbon">
    <motion.div className="flex w-max gap-10 whitespace-nowrap" animate={{ x: ["0%", "-50%"] }} transition={{ duration: 30, repeat: Infinity, ease: "linear" }}>
      {[...RIBBON, ...RIBBON].map((t, i) => (
        <span key={i} className="flex items-center gap-10 text-[0.74rem] font-semibold uppercase tracking-[0.16em] text-[#8A7F86]">
          {t} <Sparkles className="h-3.5 w-3.5 text-[#E97C6A]" />
        </span>
      ))}
    </motion.div>
  </div>
);

/* ═══════════════ 3 · OBJET D'ART (overlapping editorial split) ═══════════════ */
export const ObjetDArt = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);
  return (
    <section ref={ref} className="bg-[#FFFCFA] py-28 md:py-40" data-testid="object-section">
      <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-16 px-6 md:grid-cols-12 md:px-12 lg:px-20">
        <div className="md:col-span-4 md:col-start-1">
          <div className="md:sticky md:top-32">
            <motion.span variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-[0.68rem] font-bold uppercase tracking-[0.28em] text-[#8A7F86]">The Object</motion.span>
            <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-5 font-display text-4xl font-light leading-[1.0] tracking-tight text-[#232222] sm:text-[3.4rem]">
              Beauty you'll want <span className="font-script text-[#E97C6A]">on display.</span>
            </motion.h2>
            <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-7 max-w-xs text-base font-light leading-relaxed text-[#6E6168]">
              114 medical-grade LEDs cradled in soft, flexible silicone and finished in our signature leopard print. As considered to look at as it is to wear.
            </motion.p>
            <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <Link to="/product/single" data-testid="object-cta" className="nav-underline mt-9 inline-flex items-center gap-2 text-[0.74rem] font-semibold uppercase tracking-[0.18em] text-[#232222]">
                Explore the technology <ArrowUpRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </div>
        <div className="relative md:col-span-7 md:col-start-6">
          <motion.div style={{ y }} className="hover-zoom overflow-hidden rounded-[1.25rem]">
            <img src={M("home_object_main")} data-media-slot="home_object_main" data-media-type="image" alt="Leopard Luxe packaging" className="aspect-[4/5] w-full object-cover" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, ease }}
            className="hover-zoom absolute -bottom-10 -left-6 hidden w-[44%] overflow-hidden rounded-[1rem] border-4 border-[#FFFCFA] shadow-[0_30px_60px_-30px_rgba(35,34,34,0.55)] md:block lg:-left-16">
            <img src={M("home_object_detail")} data-media-slot="home_object_detail" data-media-type="image" alt="Leopard mask detail" className="aspect-square w-full object-cover" />
          </motion.div>
          <div className="absolute right-5 top-5 flex items-center gap-2.5 rounded-full bg-[#232222]/85 px-4 py-2 backdrop-blur-sm">
            <span className="font-display text-xl font-light text-[#FBC4A8]">114</span>
            <span className="text-[0.58rem] font-medium uppercase tracking-[0.12em] text-[#FFF0E8]">Medical-grade LEDs</span>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════ 4 · INTERACTIVE LED COLOUR CHOOSER ═══════════════ */
const WAVES = [
  { tag: "Red · 630nm", title: "Renew", color: "#E9584A", body: "Awakens collagen and elastin to firm, plump and visibly soften fine lines." },
  { tag: "Blue · 415nm", title: "Clarify", color: "#6C8FE8", body: "Quiets blemish-causing bacteria to calm breakouts and refine the look of pores." },
  { tag: "Near-Infrared · 830nm", title: "Restore", color: "#E8A06A", body: "Travels deep to speed cellular renewal and bring back a lit-from-within glow." },
];
export const ScienceInteractive = () => {
  const [active, setActive] = useState(0);
  const w = WAVES[active];
  return (
    <section className="bg-[#232222] py-28 md:py-40" data-testid="science-section">
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
        <div className="max-w-2xl">
          <span className="text-[0.68rem] font-bold uppercase tracking-[0.28em] text-[#FBC4A8]">The Science</span>
          <h2 className="mt-5 font-display text-4xl font-light leading-[1.02] tracking-tight text-[#FFFCFA] sm:text-5xl">
            Choose your <span className="font-script text-[#FBC4A8]">light.</span>
          </h2>
          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-[#C9BBB2]">
            Three clinically-studied wavelengths, eight intelligent modes. Tap a light to see how each one speaks to your skin.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-16">
          {/* Mask + live glow */}
          <div className="relative flex h-[44vh] items-center justify-center md:h-[56vh]">
            <motion.div animate={{ backgroundColor: w.color }} transition={{ duration: 0.7, ease }}
              className="pointer-events-none absolute h-[58%] w-[58%] rounded-full opacity-45 blur-[90px]" data-testid="science-glow" />
            <img src={M("home_science_mask")} alt="LED light therapy mask" className="relative z-10 w-[60%] max-w-[360px] object-contain drop-shadow-[0_30px_70px_rgba(0,0,0,0.5)]" />
          </div>

          {/* Controls */}
          <div>
            <div className="flex gap-3" role="tablist">
              {WAVES.map((it, i) => (
                <button key={it.title} onClick={() => setActive(i)} data-testid={`science-color-${i}`}
                  className={`flex flex-1 flex-col items-center gap-2.5 rounded-2xl border px-3 py-4 transition-all duration-300 ${active === i ? "border-transparent bg-white/[0.07]" : "border-white/10 hover:border-white/25"}`}>
                  <span className="h-7 w-7 rounded-full transition-transform duration-300" style={{ backgroundColor: it.color, transform: active === i ? "scale(1.15)" : "scale(1)", boxShadow: active === i ? `0 0 24px ${it.color}` : "none" }} />
                  <span className={`text-[0.6rem] font-bold uppercase tracking-[0.12em] ${active === i ? "text-[#FFFCFA]" : "text-[#9A8E88]"}`}>{it.title}</span>
                </button>
              ))}
            </div>
            <motion.div key={active} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease }}
              className="mt-9" data-testid={`wave-${w.title.toLowerCase()}`}>
              <span className="text-[0.62rem] font-bold uppercase tracking-[0.24em]" style={{ color: w.color }}>{w.tag}</span>
              <h3 className="mt-2 font-display text-5xl font-light italic text-[#FFFCFA] md:text-6xl">{w.title}</h3>
              <p className="mt-4 max-w-md text-base font-light leading-relaxed text-[#C9BBB2]">{w.body}</p>
            </motion.div>
            <p className="mt-10 border-t border-white/10 pt-6 text-[0.66rem] font-semibold uppercase tracking-[0.2em] text-[#9A8E88]">8 modes · 10 minutes a day · UV-free & heat-free</p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════ 5 · MASSIVE TYPOGRAPHIC QUOTE ═══════════════ */
export const MassiveQuote = () => (
  <section className="bg-[#FFF0E8] py-32 md:py-44" data-testid="quote-section">
    <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="mx-auto max-w-5xl px-6 text-center">
      <motion.p variants={reveal} className="font-display text-4xl font-light italic leading-[1.08] tracking-tight text-[#232222] sm:text-6xl lg:text-[5rem]">
        "97% noticed firmer, plumper skin in just <span className="font-script text-[#E97C6A]">eight weeks.</span>"
      </motion.p>
      <motion.p variants={reveal} className="mt-10 text-[0.7rem] font-semibold uppercase tracking-[0.24em] text-[#8A7F86]">Independent consumer study · 4 weeks daily use</motion.p>
    </motion.div>
  </section>
);

/* ═══════════════ 6 · REAL RESULTS (redesigned) ═══════════════ */
const STATS = [["97%", "Firmer, lifted skin"], ["91%", "Smoother texture"], ["89%", "Brighter, even tone"]];
export const ClinicalEvidence = () => (
  <section className="bg-[#FFFCFA] py-20 md:py-28" data-testid="proof-section">
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
      <div className="mx-auto max-w-2xl text-center">
        <span className="text-[0.68rem] font-bold uppercase tracking-[0.28em] text-[#8A7F86]">Real Results</span>
        <h2 className="mt-5 font-display text-4xl font-light leading-[1.02] tracking-tight text-[#232222] sm:text-5xl">
          Skin that quietly <span className="font-script text-[#E97C6A]">turns heads.</span>
        </h2>
        <p className="mx-auto mt-6 max-w-lg text-base font-light leading-relaxed text-[#6E6168]">
          No filters, no miracles, just consistency. Over 8 to 12 weeks, our community sees real, visible change, captured in real photographs.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 items-center gap-10 md:grid-cols-12 md:gap-14">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="md:col-span-5">
          <div data-testid="before-after-proof" className="relative mx-auto aspect-[4/5] max-w-[360px] overflow-hidden rounded-[1.5rem] border border-[#F2E8E6] shadow-[0_50px_100px_-50px_rgba(35,34,34,0.45)]">
            <img src={M("home_results")} data-media-slot="home_results" data-media-type="image" alt="Marion, real customer before and after 8 weeks" className="h-full w-full object-cover object-center" />
            <span className="absolute left-4 top-4 rounded-full bg-[#FFFCFA]/90 px-3 py-1 text-[0.54rem] font-bold uppercase tracking-[0.18em] text-[#232222] backdrop-blur-sm">Before</span>
            <span className="absolute bottom-4 right-4 rounded-full bg-[#E97C6A] px-3 py-1 text-[0.54rem] font-bold uppercase tracking-[0.18em] text-[#FFFCFA]">After · 8 weeks</span>
          </div>
        </motion.div>

        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="md:col-span-7">
          <motion.div variants={reveal} className="grid grid-cols-3 overflow-hidden rounded-[1.25rem] border border-[#F2E8E6]" data-testid="review-summary">
            {STATS.map(([big, small], i) => (
              <div key={small} className={`px-3 py-7 text-center ${i < 2 ? "border-r border-[#F2E8E6]" : ""}`}>
                <p className="font-display text-3xl font-light text-[#232222] md:text-4xl">{big}</p>
                <p className="mt-2 text-[0.6rem] font-medium uppercase leading-snug tracking-[0.1em] text-[#8A7F86]">{small}</p>
              </div>
            ))}
          </motion.div>
          <motion.figure variants={reveal} className="mt-6 rounded-[1.25rem] border border-[#F2E8E6] bg-[#FFF0E8] p-7">
            <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-[#E97C6A] text-[#E97C6A]" />)}</div>
            <blockquote className="mt-4 font-display text-xl font-light italic leading-snug text-[#232222]">
              "I'm 58 and I've tried everything. Eight weeks in, my skin genuinely looks brighter and more even."
            </blockquote>
            <figcaption className="mt-4 text-[0.66rem] font-semibold uppercase tracking-[0.14em] text-[#8A7F86]">Marion T. · Verified buyer · QLD</figcaption>
          </motion.figure>
          <motion.div variants={reveal}>
            <Link to="/reviews" data-testid="proof-cta" className="nav-underline mt-7 inline-flex items-center gap-2 text-[0.74rem] font-semibold uppercase tracking-[0.18em] text-[#232222]">
              Read 50+ reviews <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ═══════════════ · DESIGN STORY (empowerment + leopard + glow) ═══════════════ */
const RAY_ANGLES = [0, 18, 36, 54, 72, 90, 108, 126, 144, 162, 180];
const SunEmblem = ({ className = "" }) => (
  <svg viewBox="0 0 44 44" className={className}>
    <circle cx="22" cy="22" r="20.5" fill="none" stroke="#E97C6A" strokeWidth="0.5" opacity="0.6" />
    <line x1="10" y1="27" x2="34" y2="27" stroke="#E97C6A" strokeWidth="0.9" strokeLinecap="round" />
    <path d="M14 27 a8 8 0 0 1 16 0" fill="none" stroke="#E97C6A" strokeWidth="0.9" strokeLinecap="round" />
    {RAY_ANGLES.map((a) => {
      const r = (a * Math.PI) / 180;
      const x1 = 22 - Math.cos(r) * 10.5, y1 = 27 - Math.sin(r) * 10.5;
      const x2 = 22 - Math.cos(r) * 13.5, y2 = 27 - Math.sin(r) * 13.5;
      return <line key={a} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#E97C6A" strokeWidth="0.7" strokeLinecap="round" />;
    })}
  </svg>
);
export const DesignStory = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const sunScale = useTransform(scrollYProgress, [0, 1], [0.85, 1.25]);
  const sunRise = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);
  return (
    <section ref={ref} className="relative overflow-hidden bg-[#FFF0E8] py-28 md:py-40" data-testid="design-story-section">
      {/* repurposed glow + rising sun, integrated as a backdrop */}
      <motion.div style={{ scale: sunScale, y: sunRise }} className="pointer-events-none absolute -right-24 -top-10 h-[34vmin] w-[34vmin] opacity-[0.14] md:right-10">
        <div className="absolute inset-[12%] rounded-full bg-[#FBC4A8] blur-[70px] opacity-70" />
        <SunEmblem className="relative h-full w-full" />
      </motion.div>

      <div className="relative mx-auto grid max-w-[1500px] grid-cols-1 items-center gap-14 px-6 md:grid-cols-12 md:gap-16 md:px-12 lg:px-20">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="md:col-span-5">
          <div className="relative overflow-hidden rounded-[1.5rem] border border-[#EAD7CC]">
            <img src={M("home_story_leopard")} data-media-slot="home_story_leopard" data-media-type="image" alt="Aura Glow signature leopard print" className="aspect-[4/5] w-full object-cover" />
            <div className="absolute bottom-5 left-5 flex items-center gap-3 rounded-full bg-[#FFFCFA]/90 px-4 py-2 backdrop-blur-sm">
              <SunEmblem className="h-5 w-5" />
              <span className="text-[0.58rem] font-bold uppercase tracking-[0.16em] text-[#232222]">The signature print</span>
            </div>
          </div>
        </motion.div>

        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="md:col-span-7 md:pl-6">
          <motion.span variants={reveal} className="text-[0.68rem] font-bold uppercase tracking-[0.28em] text-[#8A7F86]">Our Design Story</motion.span>
          <motion.h2 variants={reveal} className="mt-5 font-display text-4xl font-light leading-[1.0] tracking-tight text-[#232222] sm:text-[3.4rem]">
            Made to be <span className="font-script text-[#E97C6A]">uniquely yours.</span>
          </motion.h2>
          <motion.p variants={reveal} className="mt-7 max-w-xl text-base font-light leading-relaxed text-[#6E6168] md:text-lg">
            We didn't set out to make another LED mask. We wanted something no one else could own, something personal, beautiful and unmistakably you.
          </motion.p>
          <motion.p variants={reveal} className="mt-4 max-w-xl text-base font-light leading-relaxed text-[#6E6168] md:text-lg">
            Leopard print became our symbol of exactly that. In nature, no two patterns are ever the same, just like no two women, and no two complexions. Your skin is already beautiful, and entirely your own.
          </motion.p>
          <motion.blockquote variants={reveal} className="mt-8 border-l-2 border-[#E97C6A] pl-6 font-display text-2xl font-light italic leading-snug text-[#232222]">
            Aura Glow is about feeling confident in the skin you're in, ten quiet minutes of self-belief, every single day.
          </motion.blockquote>
          <motion.div variants={reveal} className="mt-9 flex flex-wrap gap-3">
            {["Empowering", "Personal", "Unmistakably yours"].map((t) => (
              <span key={t} className="rounded-full border border-[#EAD7CC] bg-[#FFFCFA] px-5 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-[#8A6F66]">{t}</span>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};


/* ═══════════════ 7 · TRUST STRIP ═══════════════ */
const TRUST = [[Truck, "Free Express Shipping"], [RotateCcw, "30-Day Money-Back"], [ShieldCheck, "12-Month Warranty"], [CreditCard, "4 Interest-Free Payments"]];
export const TrustStrip = () => (
  <section className="border-y border-[#F2E8E6] bg-[#FFFCFA] py-14" data-testid="trust-guarantee-strip">
    <div className="mx-auto grid max-w-[1300px] grid-cols-2 gap-8 px-6 md:grid-cols-4 md:px-12">
      {TRUST.map(([Icon, t]) => (
        <div key={t} className="flex flex-col items-center gap-3 text-center">
          <Icon className="h-6 w-6 text-[#E97C6A]" strokeWidth={1.4} />
          <span className="text-[0.66rem] font-semibold uppercase tracking-[0.14em] text-[#6E6168]">{t}</span>
        </div>
      ))}
    </div>
  </section>
);

/* ═══════════════ 8 · FINAL CONVERSION (dark bookend) ═══════════════ */
export const FinalConversion = () => (
  <section className="bg-[#232222] py-32 md:py-44" data-testid="offer-section">
    <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="mx-auto max-w-3xl px-6 text-center">
      <motion.span variants={reveal} className="text-[0.7rem] font-bold uppercase tracking-[0.3em] text-[#FBC4A8]">Begin your glow</motion.span>
      <motion.h2 variants={reveal} className="mt-7 font-display text-5xl font-light leading-[0.92] tracking-tight text-[#FFFCFA] sm:text-7xl">
        Start your <span className="font-script text-[#FBC4A8]">ritual tonight.</span>
      </motion.h2>
      <motion.p variants={reveal} className="mx-auto mt-7 max-w-md text-base font-light leading-relaxed text-[#C9BBB2] md:text-lg">
        Join the women who've made ten glowing minutes the favourite part of their evening. Loved, or your money back within 30 days.
      </motion.p>
      <motion.div variants={reveal} className="mt-11 flex flex-col items-center justify-center gap-5 sm:flex-row">
        <AddToCartButton sku="single" label="Add to Bag" variant="accent" testid="secondary-cta-button" />
        <span className="font-display text-xl font-light text-[#FFFCFA]">{fmt(p.price)} {p.compareAt > p.price && <span className="text-white/45 line-through">{fmt(p.compareAt)}</span>}</span>
      </motion.div>
      <motion.p variants={reveal} className="mt-6 text-[0.64rem] uppercase tracking-[0.18em] text-[#9A8E88]">4 interest-free payments via Afterpay · PayPal</motion.p>
    </motion.div>
  </section>
);

/* ═══════════════ · WHO WE ARE (brand story) ═══════════════ */
const PILLARS = [
  [FlaskConical, "Science first", "Every wavelength is chosen from clinical research, never marketing trends."],
  [Heart, "Designed to be loved", "A device you genuinely look forward to reaching for, every single day."],
  [Sparkles, "Confidence starts with skin", "Healthy skin you feel good in, and a ritual that's entirely yours."],
];
export const WhoWeAre = () => (
  <section className="bg-[#FFF0E8] py-28 md:py-40" data-testid="about-section">
    <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
      <div className="grid grid-cols-1 items-center gap-14 md:grid-cols-12 md:gap-16">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="md:col-span-6">
          <div className="hover-zoom overflow-hidden rounded-[1.25rem] border border-[#EAD7CC]">
            <img src={M("home_whoweare")} data-media-slot="home_whoweare" data-media-type="image" alt="The Aura Glow ritual" className="aspect-[5/6] w-full object-cover" />
          </div>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="md:col-span-6 md:pl-6">
          <motion.span variants={reveal} className="text-[0.68rem] font-bold uppercase tracking-[0.28em] text-[#8A7F86]">Who We Are</motion.span>
          <motion.h2 variants={reveal} className="mt-5 font-display text-4xl font-light leading-[1.0] tracking-tight text-[#232222] sm:text-[3.4rem]">
            More than skincare.<br /><span className="font-script text-[#E97C6A]">A daily ritual.</span>
          </motion.h2>
          <motion.p variants={reveal} className="mt-7 max-w-md text-base font-light leading-relaxed text-[#6E6168] md:text-lg">
            Skincare had become overwhelming, endless products, costly clinics, empty promises. We built Aura Glow to bring genuinely clinically-backed LED light therapy into one beautiful device, simple enough for every day.
          </motion.p>
          <motion.p variants={reveal} className="mt-4 max-w-md font-display text-xl font-light italic leading-snug text-[#232222]">
            The mask is a small act of self-care, ten quiet minutes that are entirely your own.
          </motion.p>
          <motion.div variants={reveal}>
            <Link to="/about" data-testid="about-cta" className="nav-underline mt-9 inline-flex items-center gap-2 text-[0.74rem] font-semibold uppercase tracking-[0.18em] text-[#232222]">
              Read our story <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
      <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="mt-16 grid grid-cols-1 gap-5 md:mt-24 md:grid-cols-3">
        {PILLARS.map(([Icon, t, d]) => (
          <motion.div variants={reveal} key={t} className="rounded-[1.25rem] border border-[#EAD7CC] bg-[#FFFCFA] p-8">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF0E8] text-[#E97C6A]"><Icon className="h-5 w-5" strokeWidth={1.5} /></span>
            <h3 className="mt-6 font-display text-2xl font-light text-[#232222]">{t}</h3>
            <p className="mt-3 text-sm font-light leading-relaxed text-[#6E6168]">{d}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ═══════════════ · ANATOMY OF THE MASK (interactive specs) ═══════════════ */
const SPECS = [
  { icon: Lightbulb, label: "114 medical-grade LEDs", detail: "Salon-density coverage across the entire face for even, effective light.", slot: "anatomy_leds", img: IMG.product },
  { icon: Layers, label: "3 clinical wavelengths", detail: "Red 630nm, Blue 415nm and Near-Infrared 830nm, each studied for a specific result.", slot: "anatomy_wavelengths", img: IMG.deviceDetail },
  { icon: SlidersHorizontal, label: "8 smart modes", detail: "Target fine lines, breakouts, dullness or recovery, all from one device.", slot: "anatomy_modes", img: IMG.leopardSilk },
  { icon: Feather, label: "Flexible silicone", detail: "Soft, skin-safe and featherlight at just 180g, it moulds gently to your face.", slot: "anatomy_silicone", img: IMG.glowFront },
  { icon: Timer, label: "10-minute auto timer", detail: "Press start and relax, the mask powers down on its own when you're done.", slot: "anatomy_timer", img: IMG.lifestyleMask },
  { icon: Usb, label: "USB-C rechargeable", detail: "Cordless, travel-ready and UV-free, heat-free for total comfort.", slot: "anatomy_usbc", img: IMG.unboxing },
];
export const AnatomySpecs = () => {
  const [active, setActive] = useState(0);
  const spec = SPECS[active];
  return (
    <section className="bg-[#FFFCFA] py-28 md:py-40" data-testid="anatomy-section">
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
        <div className="max-w-2xl">
          <span className="text-[0.68rem] font-bold uppercase tracking-[0.28em] text-[#8A7F86]">Anatomy of the Mask</span>
          <h2 className="mt-5 font-display text-4xl font-light leading-[1.02] tracking-tight text-[#232222] sm:text-5xl">
            Every detail, <span className="font-script text-[#E97C6A]">considered.</span>
          </h2>
        </div>
        <div className="mt-14 grid grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-16">
          <motion.div key={active} initial={{ opacity: 0.4, scale: 0.99 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease }}
            className="overflow-hidden rounded-[1.25rem] border border-[#F2E8E6]">
            <img src={M(spec.slot)} data-media-slot={spec.slot} data-media-type="image" alt={spec.label} className="aspect-[4/3] w-full object-cover" />
          </motion.div>
          <div className="space-y-2.5" data-testid="anatomy-spec-list">
            {SPECS.map((spec, i) => (
              <button key={spec.label} onClick={() => setActive(i)} data-testid={`anatomy-spec-${i}`}
                className={`flex w-full items-start gap-4 rounded-2xl border px-5 py-4 text-left transition-all duration-300 ${active === i ? "border-[#E97C6A] bg-[#FFF0E8]" : "border-[#F2E8E6] bg-[#FFFCFA] hover:border-[#FBC4A8]"}`}>
                <span className={`flex h-10 w-10 flex-none items-center justify-center rounded-full transition-colors ${active === i ? "bg-[#E97C6A] text-[#FFFCFA]" : "bg-[#FFF0E8] text-[#E97C6A]"}`}>
                  <spec.icon className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.08em] text-[#232222]">{spec.label}</p>
                  {active === i && <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-1.5 text-sm font-light leading-relaxed text-[#6E6168]">{spec.detail}</motion.p>}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════ · THE PROOF (interactive graph + table) ═══════════════ */
const RESULTS = [
  ["Firmer, lifted skin", 97],
  ["Smoother texture", 91],
  ["Brighter, even tone", 89],
  ["Softer fine lines", 84],
  ["Deeper hydration", 93],
];
const COMPARE = [
  ["Price", "$349 once", "$300–600", "$200+ / visit"],
  ["Use at home", true, true, false],
  ["Signature leopard design", true, false, false],
  ["114 medical-grade LEDs", true, "Varies", true],
  ["UV-free & heat-free", true, true, "Varies"],
  ["Just 10 min a day", true, "Varies", false],
  ["30-day money-back", true, false, false],
];
const Cell = ({ v, hero }) => {
  if (v === true) return <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${hero ? "bg-[#E97C6A] text-[#FFFCFA]" : "bg-white/10 text-[#FBC4A8]"}`}><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>;
  if (v === false) return <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/5 text-white/30"><Minus className="h-3.5 w-3.5" /></span>;
  return <span className={`text-sm font-light ${hero ? "text-[#FFFCFA]" : "text-[#C9BBB2]"}`}>{v}</span>;
};
export const TheProof = () => {
  return (
    <section className="bg-[#232222] py-28 md:py-40" data-testid="proof-interactive-section">
      <div className="mx-auto max-w-[1300px] px-6 md:px-12 lg:px-20">
        <div className="max-w-xl">
          <span className="text-[0.68rem] font-bold uppercase tracking-[0.28em] text-[#FBC4A8]">The Proof</span>
          <h2 className="mt-5 font-display text-4xl font-light leading-[1.02] tracking-tight text-[#FFFCFA] sm:text-5xl">
            How we <span className="font-script text-[#FBC4A8]">compare.</span>
          </h2>
        </div>

        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease }}
          className="mt-14 overflow-x-auto" data-testid="proof-compare-table">
          <table className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr className="border-b border-white/15">
                <th className="py-5 text-left text-[0.62rem] font-bold uppercase tracking-[0.16em] text-[#9A8E88]"></th>
                <th className="rounded-t-2xl bg-white/[0.06] py-5 text-center font-display text-lg font-light text-[#FBC4A8]">Aura Glow</th>
                <th className="py-5 text-center text-[0.62rem] font-bold uppercase tracking-[0.14em] text-[#C9BBB2]">Other LED Masks</th>
                <th className="py-5 text-center text-[0.62rem] font-bold uppercase tracking-[0.14em] text-[#C9BBB2]">In-Clinic Facials</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map(([row, a, b, c], i) => (
                <tr key={row} className="border-b border-white/10">
                  <td className="py-5 pr-4 text-sm font-light text-[#FFF0E8]">{row}</td>
                  <td className={`py-5 text-center ${i === COMPARE.length - 1 ? "rounded-b-2xl" : ""} bg-white/[0.06]`}><Cell v={a} hero /></td>
                  <td className="py-5 text-center"><Cell v={b} /></td>
                  <td className="py-5 text-center"><Cell v={c} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </div>
    </section>
  );
};
